import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Grid, Paper, Typography } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  IsSmallScreen,
  updateMainHeader,
  getLocalValue,
  LoacalVariables,
} from "../../commonFunctions/CommonFunctions";
import { WSIcons } from "../../commonFunctions/iIcons";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import WSDashboardCard from "../../Components/CardListing/WSDashboardCard";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import dashboardBg from "../../images/dashboard-bg-card.svg";
import http from "../../commonFunctions/httpClient"; // Add this import

import { RecentOrders } from "./RecentOrders";
import { RecentNotification } from "./RecentNotification";
import TopProductsCard from "./ProductsCard";

import { RecentPrescriptionOrder } from "./RecentPrescriptionOrder";
import SalesBarChart from "./SalesChart";

interface OrderSummary {
  totalOrders: number;
  pendingOrders: number;
  outOfStockProducts: number;
  totalEarnings: number;
}

export const DashBoard: React.FC<any> = React.memo((props) => {
  const navigate = useNavigate();
  const isMobile = IsSmallScreen();
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  const [vendorId, setVendorId] = useState<string>("");
  const [orderSummary, setOrderSummary] = useState<OrderSummary>({
    totalOrders: 0,
    pendingOrders: 0,
    outOfStockProducts: 0,
    totalEarnings: 0,
  });
  const [loading, setLoading] = useState(true);

  // Name from the login response (stored in localStorage at login)
  const vendorName = getLocalValue(LoacalVariables.Name) || "there";

  // Today's date, e.g. "Wednesday, 16-06-2026"
  const now = new Date();
  const todayLabel = `${now.toLocaleDateString("en-US", { weekday: "long" })}, ${String(
    now.getDate()
  ).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;

  // Load vendorId on mount (same pattern as TechnicianManagement)
  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    if (userId) {
      console.log('🚀 Loading vendorId:', userId);
      setVendorId(userId);
    }
  }, []);

  // Fetch order summary from API
  const fetchOrderSummary = useCallback(async () => {
    if (!vendorId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("📡 FETCHING ORDERS:", vendorId);
      const response: any = await http.get(`pharmacy/Ph_Order_/${vendorId}/orders`);
      
      console.log('✅ DASHBOARD API RESPONSE:', response);
      
      if (response) {
        setOrderSummary({
          totalOrders: response.totalOrders || 0,
          pendingOrders: response.pendingOrders || 0,
          outOfStockProducts: response.outOfStockProducts || 0,
          totalEarnings: response.totalEarnings || 0,
        });
      }
    } catch (error: any) {
      console.error("💥 DASHBOARD API ERROR:", error);
      showMessage("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  }, [vendorId, showMessage]);

  // Fetch data when vendorId changes
  useEffect(() => {
    if (vendorId) {
      fetchOrderSummary();
    }
  }, [vendorId, fetchOrderSummary]);

  updateMainHeader("Dashboard");
  breadCrumbsStore.updateBreadCrumbs([{ Overview: "/" }]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 1, mb: 2, borderRadius: 3, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading dashboard...</Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* ---------------- Welcome Section ---------------- */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 1,
          mb: 2,
          borderRadius: 3,
          backgroundImage: `url(${dashboardBg})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              fontWeight={600}
              style={{ color: "#098963" }}
            >
              Hello, {vendorName} 👋
            </Typography>
            <Typography sx={{ color: "#022219", mt: 0.5 }}>
              Get a quick snapshot of your store's health today.
            </Typography>
            <Typography sx={{ color: "#828584", mt: 1 }}>
              {todayLabel}
            </Typography>
          </Grid>

          {/* ✅ Updated with real API data */}
          <Grid item xs={12} sm={3}>
            <WSDashboardCard
              title="Today Orders"
              value={orderSummary.totalOrders.toString()}
              percentage=""
              bottomIcon={<TrendingUpIcon />}
              icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
              onClick={() => navigate("/pharmacy-orders/list")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <WSDashboardCard
              title="Pending Orders"
              value={orderSummary.pendingOrders.toString()}
              percentage=""
              bottomIcon={<TrendingUpIcon />}
              icon={<ShoppingCartIcon sx={{ color: "#ff9800" }} />}
              onClick={() => navigate("/pharmacy-orders/list?status=pending")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <WSDashboardCard
              title="Out Of Stock"
              value={orderSummary.outOfStockProducts.toString()}
              percentage=""
              bottomIcon={<TrendingUpIcon />}
              icon={<WSIcons.OutOfStockIcon sx={{ color: "red" }} />}
              onClick={() => navigate("/vendor-product/list?stock=out")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <WSDashboardCard
              title="Total Earnings"
              value={`₹${orderSummary.totalEarnings}`}
              percentage=""
              bottomIcon={<TrendingUpIcon />}
              icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
              onClick={() => navigate("/payment/list")}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Rest of your existing layout remains the same */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Paper style={{ padding: 16 }}>
                <RecentOrders />
              </Paper>
            </Grid>
            <Grid item>
              <Paper style={{ padding: 16 }}>
                <SalesBarChart />
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Paper style={{ padding: 16 }}>
                <RecentNotification />
              </Paper>
            </Grid>
            <Grid item>
              <Paper style={{ padding: 16 }}>
                <TopProductsCard />
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <RecentPrescriptionOrder />
        </Grid>
      </Grid>
    </>
  );
});
